/**
 * AgentBroker — Ant Design 6 theme config.
 *
 * Consume in every React widget's <App.tsx>:
 *
 *   import { ConfigProvider } from 'antd';
 *   import ruRU from 'antd/locale/ru_RU';
 *   import { agentBrokerTheme } from '@shared/antd-theme';
 *
 *   <ConfigProvider locale={ruRU} theme={agentBrokerTheme}>...</ConfigProvider>
 *
 * Values are 1:1 with tokens.json / tokens.css — change there first,
 * then re-generate / sync this file.
 */

import type { ThemeConfig } from 'antd';

// --- Raw token references (kept as constants so they're greppable) ---
const brand = {
  50: '#fff1f4', 100: '#ffe1e8', 200: '#ffc2ce', 300: '#ff8ea5',
  400: '#fc5b7b', 500: '#f91942', 600: '#db1239', 700: '#b40c2e',
  800: '#8e0924', 900: '#5f0618',
} as const;

const gray = {
  25: '#fbfbfc', 50: '#f7f7f8', 100: '#eeeef1', 200: '#e2e2e6',
  300: '#cfcfd6', 400: '#a1a1ab', 500: '#71717a', 600: '#52525b',
  700: '#3f3f46', 800: '#27272a', 900: '#18181b',
} as const;

const status = {
  success: '#2f9e44',
  warning: '#e8890b',
  error:   '#dc2626',
  info:    '#1971c2',
} as const;

export const agentBrokerTheme: ThemeConfig = {
  // Режим CSS-переменных (:where()-переменные для смены темы / SSR).
  // В antd 6 булев вид `cssVar: true` из типа убран — принимается только объект,
  // а пустой объект и есть «включено с префиксом по умолчанию (ant)», т.е.
  // ровно прежний смысл `true`. Правка чисто типовая, поведение не меняет.
  cssVar: {},
  hashed: true,
  token: {
    // — Brand / status —
    colorPrimary: brand[500],
    colorSuccess: status.success,
    colorWarning: status.warning,
    colorError:   status.error,
    colorInfo:    status.info,
    colorLink:    brand[600],
    colorLinkHover:  brand[700],
    colorLinkActive: brand[800],

    // — Neutrals (use our gray scale, not Antd defaults) —
    colorText:            gray[800],
    colorTextSecondary:   gray[600],
    colorTextTertiary:    gray[500],
    colorTextQuaternary:  gray[400],
    colorBorder:          gray[200],
    colorBorderSecondary: gray[100],
    colorFill:            gray[100],
    colorFillSecondary:   gray[50],
    colorFillTertiary:    gray[50],
    colorFillQuaternary:  gray[25],
    colorBgContainer:     '#ffffff',
    colorBgElevated:      '#ffffff',
    colorBgLayout:        gray[50],
    colorBgSpotlight:     gray[800],

    // — Typography —
    fontFamily:
      '"Inter", "Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontFamilyCode:
      'ui-monospace, SFMono-Regular, "JetBrains Mono", Menlo, monospace',
    fontSize:   14,
    fontSizeSM: 13,
    fontSizeLG: 16,
    fontSizeXL: 18,
    fontSizeHeading1: 30,
    fontSizeHeading2: 24,
    fontSizeHeading3: 20,
    fontSizeHeading4: 16,
    fontSizeHeading5: 14,
    lineHeight:   1.5,
    lineHeightLG: 1.5,
    lineHeightSM: 1.5,

    // — Radius —
    borderRadius:   6,
    borderRadiusSM: 4,
    borderRadiusLG: 10,
    borderRadiusXS: 4,

    // — Spacing / sizing —
    sizeUnit: 4,
    sizeStep: 4,
    controlHeight:    36,
    controlHeightSM:  30,
    controlHeightLG:  44,

    // — Motion / shadows —
    motionDurationFast: '0.12s',
    motionDurationMid:  '0.18s',
    motionDurationSlow: '0.24s',
    boxShadow:          '0 2px 6px rgba(15, 23, 42, 0.06), 0 4px 12px rgba(15, 23, 42, 0.05)',
    boxShadowSecondary: '0 8px 20px rgba(15, 23, 42, 0.08), 0 2px 6px rgba(15, 23, 42, 0.04)',
    boxShadowTertiary:  '0 1px 2px rgba(15, 23, 42, 0.06)',

    // — Wireframe off, so Antd feels filled/modern by default —
    wireframe: false,
  },
  components: {
    Button: {
      fontWeight: 500,
      controlHeight: 36,
      primaryShadow:   '0 1px 2px rgba(249, 25, 66, 0.24)',
      defaultShadow:   '0 1px 2px rgba(15, 23, 42, 0.04)',
      dangerShadow:    '0 1px 2px rgba(220, 38, 38, 0.24)',
    },
    Input: {
      controlHeight: 36,
      paddingBlock:  8,
      activeShadow:  '0 0 0 3px rgba(249, 25, 66, 0.16)',
      errorActiveShadow: '0 0 0 3px rgba(220, 38, 38, 0.16)',
    },
    Select: {
      controlHeight: 36,
    },
    Table: {
      headerBg:         gray[50],
      headerColor:      gray[700],
      headerSplitColor: gray[200],
      rowHoverBg:       gray[50],
      borderColor:      gray[200],
      cellPaddingBlock: 12,
    },
    Tabs: {
      itemColor:         gray[600],
      itemSelectedColor: brand[600],
      itemHoverColor:    brand[500],
      inkBarColor:       brand[500],
      titleFontSize:     14,
    },
    Card: {
      colorBorderSecondary: gray[200],
      paddingLG: 24,
    },
    Menu: {
      itemSelectedBg:    brand[50],
      itemSelectedColor: brand[600],
      itemHoverBg:       gray[100],
      itemBorderRadius:  6,
    },
    Tag: {
      defaultBg: gray[100],
      defaultColor: gray[700],
    },
    Alert: {
      colorInfoBg:    '#e7f1fb',
      colorSuccessBg: '#e8f5ec',
      colorWarningBg: '#fff4e0',
      colorErrorBg:   '#fdecec',
    },
    Modal: {
      borderRadiusLG: 12,
      paddingContentHorizontalLG: 24,
    },
    Layout: {
      headerBg:      '#ffffff',
      headerHeight:  64,
      siderBg:       gray[900],
      triggerBg:     gray[800],
      bodyBg:        gray[50],
    },
    Form: {
      labelFontSize:  13,
      labelColor:     gray[700],
      itemMarginBottom: 20,
    },
  },
};

export default agentBrokerTheme;
